/**
 * lib/authSync.js · v2.7.63+E46.5 · Cross-Screen Session Persistence
 * ═══════════════════════════════════════════════════════════════════
 * Broadcasts auth-state changes across all open tabs/windows of the same
 * origin so dispatchers running 3–4 monitors never need to re-login on
 * every screen.
 *
 * Mechanism (defense in depth):
 *   1. BroadcastChannel('rtg-auth') — instant delivery, modern browsers
 *   2. `storage` event listener on window — automatic cross-tab
 *      propagation via localStorage mutations (older browsers + safety
 *      net when BroadcastChannel is unavailable)
 *   3. `visibilitychange` handler — silently re-check token on tab
 *      refocus so a stale/expired session gets caught before the user
 *      clicks and hits a 401
 *
 * Usage:
 *   import { broadcastLogin, broadcastLogout, installAuthSync }
 *     from "@/lib/authSync";
 *
 *   // At app boot:
 *   installAuthSync({ scope: "driver" });   // for /lib/api.js
 *   installAuthSync({ scope: "admin" });    // for /lib/adminApi.js
 *
 *   // When user logs in:
 *   broadcastLogin("driver", { token, user });
 *
 *   // When user logs out or 401 fires:
 *   broadcastLogout("driver");
 */

const CHANNEL_NAME = "rtg-auth";
const _channels = new Map();  // one channel per scope so listeners don't cross-talk

function _channel(scope) {
  if (typeof window === "undefined") return null;
  if (typeof BroadcastChannel === "undefined") return null;
  if (!_channels.has(scope)) {
    try {
      _channels.set(scope, new BroadcastChannel(`${CHANNEL_NAME}:${scope}`));
    } catch (_e) {
      _channels.set(scope, null);
    }
  }
  return _channels.get(scope);
}

// Which localStorage keys hold the auth state for each scope. Kept in
// one place so a future rename doesn't have to touch three files.
const KEYS = {
  driver: { token: "rtg_token",       user: "rtg_driver" },
  admin:  { token: "rtg_admin_token", user: "rtg_admin"  },
};

export function broadcastLogin(scope, { token, user } = {}) {
  const ch = _channel(scope);
  if (!ch) return;
  try {
    ch.postMessage({ type: "login", scope, token: token || null, user: user || null });
  } catch (_e) { /* noop */ }
}

export function broadcastLogout(scope) {
  const ch = _channel(scope);
  if (!ch) return;
  try {
    ch.postMessage({ type: "logout", scope });
  } catch (_e) { /* noop */ }
}

/**
 * Attach cross-tab listeners for a given auth scope. Idempotent — safe
 * to call multiple times; internal guard prevents duplicate handlers.
 *
 * When a LOGIN event arrives from another tab:
 *   • The receiving tab writes token + user into its own localStorage
 *     (silently — no reload) so the axios interceptor picks them up
 *     on the next request.
 *
 * When a LOGOUT event arrives:
 *   • The receiving tab clears its localStorage.
 *   • If it's currently on a protected route, it navigates to the
 *     scope's login page. Home / marketing routes are left alone so a
 *     staff member reading a public help doc on Screen 2 while logging
 *     out of Screen 1 doesn't get their tab yanked to /admin/login.
 */
export function installAuthSync({ scope = "driver" } = {}) {
  if (typeof window === "undefined") return () => {};
  const k = KEYS[scope];
  if (!k) return () => {};
  const flag = `__rtgAuthSync_${scope}_installed`;
  if (window[flag]) return () => {};
  window[flag] = true;

  // v2.7.65+E194 · Anonymous-visitor logout fix. The previous logic
  // treated everything NOT in a small public-prefix list as a "protected
  // driver route" — which incorrectly captured every marketing page
  // (/about, /services, /rates, /contact, /tourism/*, /drive-for-us,
  // /voucher-program, /my-vouchers, /leadership, /drivers,
  // /bison-taxi-commander, /vehicles, etc.). Anonymous visitors on
  // those pages have no rtg_token, so any background 401 or a return-
  // from-idle visibilitychange would fire applyLogout() → /login.
  //
  // Fixed by inverting the check: ONLY treat known driver-app routes as
  // protected. Everything else — including all marketing pages, admin
  // routes (their own scope), and unknown paths — is left alone.
  const DRIVER_APP_PREFIXES = [
    "/dashboard", "/city", "/new-trip", "/queue", "/custom",
    "/meter", "/meter-statewide", "/trips", "/fleet", "/profile",
    "/earnings", "/end-of-day", "/delivery/active", "/onboarding/contractor-setup",
  ];
  const isProtectedRoute = () => {
    const p = window.location.pathname;
    if (scope === "admin") return p.startsWith("/admin") && !p.startsWith("/admin/login");
    // Driver scope: use a WHITELIST of driver-app routes. Never react
    // to marketing / customer / admin / auth / unknown paths.
    if (p.startsWith("/admin")) return false;
    return DRIVER_APP_PREFIXES.some((pref) =>
      p === pref || p.startsWith(pref + "/") || p.startsWith(pref)
    );
  };
  const loginPath = scope === "admin" ? "/admin/login" : "/login";

  const applyLogin = ({ token, user }) => {
    if (token) localStorage.setItem(k.token, token);
    if (user)  localStorage.setItem(k.user, typeof user === "string" ? user : JSON.stringify(user));
    // v2.7.65+E151 · Notify same-tab React contexts. Storage events don't
    // fire in the tab that wrote the value, and BroadcastChannel handlers
    // run in the receiver tab — so without a same-tab signal the receiver
    // tab's AuthContext/AdminContext won't refresh until the next axios
    // request. Custom event bridges that gap.
    try {
      window.dispatchEvent(new CustomEvent(`rtg-auth-updated:${scope}`, {
        detail: { type: "login", token: token || null, user: user || null },
      }));
    } catch (_e) { /* noop */ }
  };
  const applyLogout = () => {
    localStorage.removeItem(k.token);
    localStorage.removeItem(k.user);
    try {
      window.dispatchEvent(new CustomEvent(`rtg-auth-updated:${scope}`, {
        detail: { type: "logout" },
      }));
    } catch (_e) { /* noop */ }
    if (isProtectedRoute() && window.location.pathname !== loginPath) {
      window.location.href = loginPath;
    }
  };

  // ── BroadcastChannel — primary path ──
  const ch = _channel(scope);
  const onMsg = (ev) => {
    const data = ev?.data || {};
    if (data.scope && data.scope !== scope) return;
    if (data.type === "login")  applyLogin(data);
    if (data.type === "logout") applyLogout();
  };
  if (ch) ch.addEventListener("message", onMsg);

  // ── storage event — fallback + safety net ──
  // Fires in OTHER tabs when this tab mutates localStorage. Note it does
  // NOT fire in the tab that made the change, which is exactly what we
  // want (no self-echo).
  const onStorage = (ev) => {
    if (!ev.key) return;
    if (ev.key !== k.token) return;
    if (ev.newValue == null && ev.oldValue != null) {
      applyLogout();
    }
    // (login-via-storage is a no-op: the token is already written; the
    //  next axios request picks it up automatically.)
  };
  window.addEventListener("storage", onStorage);

  // ── visibilitychange — silent revalidation on refocus ──
  // v2.7.65+E187 · Hardened for multi-monitor / minimize resilience.
  // A tab returning from hidden (minimize, background, other-monitor
  // switch) must NEVER force a logout on its own. We only apply logout
  // if we can confirm the token was actually cleared in another tab
  // AND we're on a protected route. If the token is still present, we
  // do nothing — a real auth failure will surface organically via the
  // next axios call and be handled by the session-verify interceptor.
  const onVisibility = () => {
    if (document.visibilityState !== "visible") return;
    const tok = localStorage.getItem(k.token);
    // Token still present → session intact; no action needed.
    if (tok) return;
    // Token missing and we're on a protected route → another tab
    // logged out while this tab was hidden.
    if (isProtectedRoute()) {
      applyLogout();
    }
  };
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    if (ch) ch.removeEventListener("message", onMsg);
    window.removeEventListener("storage", onStorage);
    document.removeEventListener("visibilitychange", onVisibility);
    window[flag] = false;
  };
}
